import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowRight,
  FaChartLine,
  FaClock,
  FaClipboardList,
  FaDollarSign,
  FaShippingFast,
  FaSyncAlt,
  FaUserShield,
  FaUsers,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  getDashboardSummary,
  getDeliveryStats,
  getOrderMetrics,
  getTodaysSales,
  getUserStats,
  getVisitorStats,
} from "../../api/dashboard";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("en-US");

const Dashboard = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [todaysSales, setTodaysSales] = useState({
    total_orders: 0,
    total_revenue: 0,
    paid_revenue: 0,
    paid_orders: 0,
    pending_orders: 0,
  });
  const [deliveryStats, setDeliveryStats] = useState([]);
  const [visitorStats, setVisitorStats] = useState({
    total_visitors: 0,
    visitors_this_week: 0,
    visitors_today: 0,
  });
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadDashboard = () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setMessages(["You are not logged in. Please sign in as an admin to view the dashboard."]);
      setLoading(false);
      return Promise.resolve();
    }

    setLoading(true);

    const requests = [
      getDashboardSummary(token),
      getOrderMetrics(token),
      getUserStats(token),
      getTodaysSales(token),
      getDeliveryStats(token),
      getVisitorStats(token),
    ];

    Promise.allSettled(requests)
      .then((results) => {
        const nextMessages = [];

        if (results[0].status === "fulfilled" && results[0].value) {
          const summary = results[0].value;
          setChartData(Array.isArray(summary.chartData) ? summary.chartData : []);
          setUserStats(Array.isArray(summary.userStats) ? summary.userStats : []);
          setTodaysSales(summary.todaysSales || {});
          setDeliveryStats(Array.isArray(summary.deliveryStats) ? summary.deliveryStats : []);
          setVisitorStats(summary.visitorStats || {});
        } else {
          nextMessages.push("Unified dashboard summary is unavailable. Using fallback data sources.");
        }

        if (results[1].status === "fulfilled" && results[0].status !== "fulfilled") {
          setChartData(Array.isArray(results[1].value) ? results[1].value : []);
        } else {
          if (results[1].status !== "fulfilled" && results[0].status !== "fulfilled") {
            nextMessages.push("Order trend data could not be loaded.");
          }
        }

        if (results[2].status === "fulfilled" && results[0].status !== "fulfilled") {
          setUserStats(Array.isArray(results[2].value) ? results[2].value : []);
        } else if (results[2].status !== "fulfilled" && results[0].status !== "fulfilled") {
          nextMessages.push("User statistics are currently unavailable.");
        }

        if (results[3].status === "fulfilled" && results[0].status !== "fulfilled") {
          setTodaysSales(results[3].value || {});
        } else if (results[3].status !== "fulfilled" && results[0].status !== "fulfilled") {
          nextMessages.push("Today's sales snapshot could not be loaded.");
        }

        if (results[4].status === "fulfilled" && results[0].status !== "fulfilled") {
          setDeliveryStats(Array.isArray(results[4].value) ? results[4].value : []);
        } else if (results[4].status !== "fulfilled" && results[0].status !== "fulfilled") {
          nextMessages.push("Delivery statistics are currently unavailable.");
        }

        if (results[5].status === "fulfilled" && results[0].status !== "fulfilled") {
          setVisitorStats(results[5].value || {});
        } else if (results[5].status !== "fulfilled" && results[0].status !== "fulfilled") {
          nextMessages.push("Visitor statistics are currently unavailable.");
        }

        setMessages(nextMessages);
        setLastUpdated(new Date());
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const totals = useMemo(() => {
    const totalOrders = chartData.reduce((sum, item) => sum + Number(item.orders || 0), 0);
    const totalRevenue = chartData.reduce((sum, item) => sum + Number(item.revenue || 0), 0);
    const totalUsers = userStats.reduce((sum, item) => sum + Number(item.count || 0), 0);

    return { totalOrders, totalRevenue, totalUsers };
  }, [chartData, userStats]);

  const averageOrderValue = useMemo(() => {
    const orders = Number(todaysSales.total_orders || 0);
    const revenue = Number(todaysSales.total_revenue || 0);
    return orders > 0 ? revenue / orders : 0;
  }, [todaysSales]);

  const trendSummary = useMemo(() => {
    if (!chartData.length) {
      return {
        bestDay: "No trend data yet",
        bestDayRevenue: 0,
        orderPulse: 0,
      };
    }

    const bestEntry = [...chartData].sort(
      (a, b) => Number(b.revenue || 0) - Number(a.revenue || 0)
    )[0];

    const orderPulse =
      chartData.reduce((sum, item) => sum + Number(item.orders || 0), 0) / chartData.length;

    return {
      bestDay: bestEntry?.day || bestEntry?.name || "No data",
      bestDayRevenue: Number(bestEntry?.revenue || 0),
      orderPulse,
    };
  }, [chartData]);

  const roleBreakdown = useMemo(
    () => (userStats.length ? userStats : [{ role: "No role data available yet", count: 0 }]),
    [userStats]
  );

  const deliveryBreakdown = useMemo(
    () =>
      deliveryStats.length
        ? deliveryStats
        : [{ delivery_status: "No delivery summary available yet", count: 0 }],
    [deliveryStats]
  );

  const deliveryTotals = useMemo(() => {
    return deliveryStats.reduce(
      (acc, item) => {
        const key = String(item.delivery_status || "").toLowerCase();
        const count = Number(item.count || 0);

        if (["pending", "processing"].includes(key)) acc.pending += count;
        if (["shipped", "assigned", "in_transit", "out_for_delivery"].includes(key)) {
          acc.active += count;
        }
        if (key === "delivered") acc.delivered += count;
        if (["cancelled", "failed"].includes(key)) acc.blocked += count;

        acc.total += count;
        return acc;
      },
      { pending: 0, active: 0, delivered: 0, blocked: 0, total: 0 }
    );
  }, [deliveryStats]);

  const roleTotal = useMemo(
    () => roleBreakdown.reduce((sum, item) => sum + Number(item.count || 0), 0),
    [roleBreakdown]
  );

  const roleLead = useMemo(() => {
    const topRole = [...roleBreakdown].sort(
      (a, b) => Number(b.count || 0) - Number(a.count || 0)
    )[0];

    const leadCount = Number(topRole?.count || 0);

    return {
      role: topRole?.role || "No role data",
      count: leadCount,
      share: roleTotal ? (leadCount / roleTotal) * 100 : 0,
    };
  }, [roleBreakdown, roleTotal]);

  const cards = [
    {
      title: "Revenue today",
      value: currency.format(Number(todaysSales.total_revenue || 0)),
      meta: "Daily sales performance",
      icon: <FaDollarSign />,
      tone: "gold",
    },
    {
      title: "Orders today",
      value: numberFormatter.format(Number(todaysSales.total_orders || 0)),
      meta: "Orders placed in the current cycle",
      icon: <FaClipboardList />,
      tone: "emerald",
    },
    {
      title: "Visitors today",
      value: numberFormatter.format(Number(visitorStats.visitors_today || 0)),
      meta: "Traffic reaching the storefront today",
      icon: <FaChartLine />,
      tone: "ink",
    },
    {
      title: "Customer base",
      value: numberFormatter.format(totals.totalUsers),
      meta: "Registered users across the platform",
      icon: <FaUsers />,
      tone: "slate",
    },
  ];

  const quickLinks = [
    {
      to: "/admin/products",
      label: "Catalogue control",
      note: "Update products, pricing, and stock presentation.",
    },
    {
      to: "/admin/orders",
      label: "Order desk",
      note: "Monitor fulfilment flow and payment progress.",
    },
    {
      to: "/admin/users",
      label: "Client records",
      note: "Review customers, staff roles, and access.",
    },
    {
      to: "/admin/design",
      label: "Design management",
      note: "Handle design assets and creative operations.",
    },
    {
      to: "/delivery/admin",
      label: "Delivery dispatch",
      note: "Assign couriers and keep fulfilment moving.",
    },
  ];

  const spotlight = [
    {
      label: "Paid revenue",
      value: currency.format(Number(todaysSales.paid_revenue || 0)),
    },
    {
      label: "Pending orders",
      value: numberFormatter.format(Number(todaysSales.pending_orders || 0)),
    },
    {
      label: "Average order value",
      value: currency.format(averageOrderValue),
    },
    {
      label: "Weekly visitors",
      value: numberFormatter.format(Number(visitorStats.visitors_this_week || 0)),
    },
  ];

  const workflowCards = [
    {
      title: "Orders waiting",
      value: numberFormatter.format(Number(todaysSales.pending_orders || 0)),
      note: "Demand that still needs the next admin action.",
      to: "/admin/orders",
      icon: <FaClipboardList />,
      tone: "gold",
    },
    {
      title: "Dispatch active",
      value: numberFormatter.format(deliveryTotals.active),
      note: "Deliveries currently moving through the route pipeline.",
      to: "/delivery/admin",
      icon: <FaShippingFast />,
      tone: "emerald",
    },
    {
      title: "Leading user role",
      value: roleLead.role,
      note: `${numberFormatter.format(roleLead.count)} accounts, ${roleLead.share.toFixed(0)}% share.`,
      to: "/admin/users",
      icon: <FaUserShield />,
      tone: "ink",
    },
  ];

  return (
    <div className="roha-admin-dashboard">
      <section className="roha-admin-dashboard__hero roha-admin-dashboard__hero--executive">
        <div className="roha-admin-dashboard__hero-copy">
          <p className="roha-admin-navbar__eyebrow">Executive overview</p>
          <h2>Professional commerce command center for Roha Jewellery.</h2>
          <p>
            Track revenue, operational pressure, traffic quality, and team priorities from one
            cleaner admin experience.
          </p>

          <div className="roha-admin-dashboard__hero-actions">
            <button
              type="button"
              className="roha-admin-navbar__chip roha-admin-navbar__chip--primary"
              onClick={() => navigate("/admin/orders")}
            >
              Open order desk <FaArrowRight />
            </button>
            <button
              type="button"
              className="roha-admin-navbar__chip"
              onClick={() => navigate("/admin/products")}
            >
              Manage catalogue
            </button>
            <button type="button" className="roha-admin-navbar__chip" onClick={loadDashboard}>
              Refresh board <FaSyncAlt />
            </button>
          </div>
        </div>

        <div className="roha-admin-dashboard__hero-side">
          <div className="roha-admin-executive-card">
            <span>Revenue window</span>
            <strong>{currency.format(Number(totals.totalRevenue || 0))}</strong>
            <p>
              {numberFormatter.format(totals.totalOrders)} tracked orders across the active trend
              window.
            </p>
          </div>

          <div className="roha-admin-executive-grid">
            <article>
              <span>Best day</span>
              <strong>{trendSummary.bestDay}</strong>
              <p>{currency.format(trendSummary.bestDayRevenue)}</p>
            </article>
            <article>
              <span>Order pulse</span>
              <strong>{trendSummary.orderPulse.toFixed(1)}</strong>
              <p>avg orders per trend day</p>
            </article>
            <article>
              <span>Delivery states</span>
              <strong>{numberFormatter.format(deliveryStats.length)}</strong>
              <p>statuses currently tracked</p>
            </article>
            <article>
              <span>Total visitors</span>
              <strong>{numberFormatter.format(Number(visitorStats.total_visitors || 0))}</strong>
              <p>platform reach so far</p>
            </article>
          </div>
        </div>
      </section>

      <section className="roha-admin-dashboard__pulse">
        <div className="roha-admin-dashboard__pulse-item">
          <span><FaClock /> Last refresh</span>
          <strong>{lastUpdated ? lastUpdated.toLocaleTimeString() : "Waiting for first load"}</strong>
        </div>
        <div className="roha-admin-dashboard__pulse-item">
          <span><FaClipboardList /> Open order pressure</span>
          <strong>{numberFormatter.format(Number(todaysSales.pending_orders || 0))} pending today</strong>
        </div>
        <div className="roha-admin-dashboard__pulse-item">
          <span><FaShippingFast /> Dispatch overview</span>
          <strong>
            {numberFormatter.format(deliveryTotals.active)} active, {numberFormatter.format(deliveryTotals.pending)} queued
          </strong>
        </div>
      </section>

      {messages.length > 0 && (
        <section className="roha-admin-panel roha-admin-panel--notice">
          <div className="roha-admin-panel__header">
            <h3>Operational notices</h3>
            <p>Some optional reporting services are unavailable right now.</p>
          </div>
          <div className="roha-admin-notice-list">
            {messages.map((message) => (
              <div key={message} className="roha-admin-notice-pill">
                {message}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="roha-admin-panels roha-admin-panels--workflow">
        {workflowCards.map((item) => (
          <button
            type="button"
            key={item.title}
            className={`roha-admin-workflow-card roha-admin-workflow-card--${item.tone}`}
            onClick={() => navigate(item.to)}
          >
            <div className="roha-admin-workflow-card__icon">{item.icon}</div>
            <div className="roha-admin-workflow-card__body">
              <span>{item.title}</span>
              <strong>{loading ? "..." : item.value}</strong>
              <p>{item.note}</p>
            </div>
            <div className="roha-admin-workflow-card__cta">
              Open <FaArrowRight />
            </div>
          </button>
        ))}
      </section>

      <section className="roha-admin-card-grid">
        {cards.map((card) => (
          <article key={card.title} className={`roha-admin-stat roha-admin-stat--${card.tone}`}>
            <div className="roha-admin-stat__icon">{card.icon}</div>
            <div className="roha-admin-stat__body">
              <span>{card.title}</span>
              <strong>{loading ? "..." : card.value}</strong>
              <p>{card.meta}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="roha-admin-panels roha-admin-panels--asymmetric">
        <article className="roha-admin-panel roha-admin-panel--spotlight">
          <div className="roha-admin-panel__header">
            <h3>Commercial spotlight</h3>
            <p>Today&apos;s strongest business signals</p>
          </div>
          <div className="roha-admin-spotlight-grid">
            {spotlight.map((item) => (
              <div key={item.label} className="roha-admin-spotlight-item">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="roha-admin-panel">
          <div className="roha-admin-panel__header">
            <h3>Priority actions</h3>
            <p>Move quickly between the areas that need attention</p>
          </div>
          <div className="roha-admin-action-list">
            {quickLinks.map((item) => (
              <button
                key={item.to}
                type="button"
                className="roha-admin-action-card"
                onClick={() => navigate(item.to)}
              >
                <div>
                  <strong>{item.label}</strong>
                  <p>{item.note}</p>
                </div>
                <span>
                  Open <FaArrowRight />
                </span>
              </button>
            ))}
          </div>
        </article>
      </section>

      <section className="roha-admin-panels">
        <article className="roha-admin-panel">
          <div className="roha-admin-panel__header">
            <h3>User structure</h3>
            <p>Role balance across the platform</p>
          </div>
          <div className="roha-admin-list roha-admin-list--metrics">
            {roleBreakdown.map((item) => (
              <div key={item.role} className="roha-admin-list__item">
                <div>
                  <span>{item.role}</span>
                  <small>Platform role allocation</small>
                </div>
                <strong>{numberFormatter.format(Number(item.count || 0))}</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="roha-admin-panel">
          <div className="roha-admin-panel__header">
            <h3>Delivery operations</h3>
            <p>Status distribution across fulfilment</p>
          </div>
          <div className="roha-admin-list roha-admin-list--metrics">
            {deliveryBreakdown.map((item) => (
              <div key={item.delivery_status} className="roha-admin-list__item">
                <div>
                  <span>{item.delivery_status}</span>
                  <small>Current delivery workflow count</small>
                </div>
                <strong>{numberFormatter.format(Number(item.count || 0))}</strong>
              </div>
            ))}
          </div>
        </article>

        <article className="roha-admin-panel">
          <div className="roha-admin-panel__header">
            <h3>Fulfilment health</h3>
            <p>Operational balance across the delivery workflow</p>
          </div>
          <div className="roha-admin-kpis">
            <div>
              <strong>{numberFormatter.format(deliveryTotals.pending)}</strong>
              <span>Pending dispatch</span>
            </div>
            <div>
              <strong>{numberFormatter.format(deliveryTotals.active)}</strong>
              <span>Active deliveries</span>
            </div>
            <div>
              <strong>{numberFormatter.format(deliveryTotals.delivered)}</strong>
              <span>Completed deliveries</span>
            </div>
            <div>
              <strong>{numberFormatter.format(deliveryTotals.blocked)}</strong>
              <span>Blocked or cancelled</span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default Dashboard;
